import { Component } from '@theme/component';
import { readAssistantSession, saveAssistantSession } from './hflex-assistant-session.js';

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLButtonElement} launcher
 * @property {HTMLTextAreaElement} input
 * @property {HTMLButtonElement} send
 * @property {HTMLButtonElement} reset
 * @property {HTMLElement} messages
 * @property {HTMLElement} status
 * @extends Component<Refs>
 */
class HFlexAssistant extends Component {
  requiredRefs = ['dialog', 'launcher', 'input', 'send', 'reset', 'messages', 'status'];
  #history = [];
  #request;
  #requestTimer;
  #welcome = '';
  #busy = false;
  #transcript = [];
  #pendingContent = '';
  #restored = false;
  #lifecycle;
  #restoreRequest;
  #generation = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#welcome ||= this.querySelector('[data-welcome]')?.textContent ?? '';
    this.refs.launcher.hidden = false;
    this.#lifecycle?.abort();
    this.#lifecycle = new AbortController();
    const options = { signal: this.#lifecycle.signal };
    window.addEventListener('pagehide', () => { this.#persist(); this.#stopPending(); }, options);
    window.addEventListener('pageshow', event => { if (event.persisted) this.#restore(); }, options);
    this.refs.dialog.addEventListener('close', () => this.#persist(), options);
    this.refs.input.addEventListener('input', () => this.#persist(), options);
    this.refs.input.addEventListener('keydown', event => {
      if (event.key !== 'Enter' || event.shiftKey || event.isComposing || event.keyCode === 229) return;
      event.preventDefault();
      if (!event.repeat) void this.submit(event);
    }, options);
    if (!this.#restored) { this.#restored = true; this.#restore(); }
  }

  #persist() {
    try {
      saveAssistantSession(window.sessionStorage, this.dataset.endpoint, {
        history: this.#history, transcript: this.#transcript,
        draft: this.refs.input.value || this.#pendingContent, open: this.refs.dialog.open,
      });
    } catch { /* The assistant still works when browser storage is blocked. */ }
  }

  #stopPending() {
    this.#generation++;
    clearTimeout(this.#requestTimer);
    this.#request?.abort();
    this.#restoreRequest?.abort();
    this.#busy = false;
    delete this.dataset.working;
    this.refs.send.disabled = false;
    this.refs.reset.disabled = false;
    this.refs.messages.removeAttribute('aria-busy');
    this.refs.status.textContent = '';
  }

  #restore() {
    let saved;
    try { saved = readAssistantSession(window.sessionStorage, this.dataset.endpoint); } catch { return; }
    if (!saved) return;
    this.#stopPending();
    this.#pendingContent = '';
    this.refs.messages.replaceChildren();
    this.#message(this.#welcome, 'assistant');
    this.#history = saved.history;
    this.#transcript = saved.transcript;
    this.refs.input.value = saved.draft;
    this.#restoreRequest = new AbortController();
    for (const entry of this.#transcript) {
      this.#message(entry.content, entry.role);
      this.#guides(entry.guides);
      if (entry.products?.length) {
        const target = document.createElement('div');
        this.refs.messages.append(target);
        void this.#products(entry.products, this.#restoreRequest.signal, target);
      }
    }
    if (saved.open) this.open();
    else if (this.refs.dialog.open) this.refs.dialog.close();
  }

  disconnectedCallback() {
    clearTimeout(this.#requestTimer);
    this.#persist();
    this.#stopPending();
    this.#lifecycle?.abort();
    super.disconnectedCallback();
  }

  open() { if (!this.refs.dialog.open) this.refs.dialog.showModal(); this.refs.input.focus(); this.#persist(); }
  close() { this.refs.dialog.close(); this.refs.launcher.focus(); }
  reset() {
    if (this.#busy) return;
    this.#restoreRequest?.abort();
    this.#history = [];
    this.#transcript = [];
    this.#pendingContent = '';
    this.refs.messages.replaceChildren();
    this.#message(this.#welcome, 'assistant');
    this.refs.status.textContent = '';
    this.refs.input.value = '';
    this.refs.input.focus();
    this.#persist();
  }

  #message(text, role) {
    const node = document.createElement('p');
    node.className = `hflex-guide__message hflex-guide__message--${role}`;
    node.textContent = text;
    this.refs.messages.append(node);
    this.refs.messages.scrollTop = this.refs.messages.scrollHeight;
    return node;
  }

  /** @param {SubmitEvent} event */
  async submit(event) {
    event.preventDefault();
    if (this.#busy) return;
    const content = this.refs.input.value.trim();
    if (!content || content.length > 1200) return;
    const generation = ++this.#generation;
    this.#busy = true;
    this.dataset.working = 'true';
    this.refs.send.disabled = true;
    this.refs.reset.disabled = true;
    this.refs.messages.setAttribute('aria-busy', 'true');
    this.refs.status.textContent = this.dataset.busy;
    this.refs.status.scrollIntoView?.({ block: 'nearest' });
    const question = this.#message(content, 'user');
    this.#pendingContent = content;
    this.refs.input.value = '';
    this.#persist();
    this.#request = new AbortController();
    const signal = this.#request.signal;
    this.#requestTimer = setTimeout(() => this.#request.abort(), 60000);
    const recent = this.#history.slice(-4);
    const first = this.#history[0];
    const context = first && !recent.includes(first) ? [first, ...recent] : recent;
    const messages = [...context, { role: 'user', content }];
    try {
      const endpoint = new URL(this.dataset.endpoint);
      if (endpoint.protocol !== 'https:') throw new Error('Invalid endpoint');
      const response = await fetch(endpoint, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'omit',
        body: JSON.stringify({ messages, ...(this.dataset.productHandle ? { productHandle: this.dataset.productHandle } : {}) }), signal,
      });
      const result = await response.json();
      if (generation !== this.#generation) return;
      if (!response.ok) throw new Error(result.error || this.dataset.error);
      this.#message(result.message, 'assistant');
      this.#guides(result.guides);
      const conversation = [...messages, { role: 'assistant', content: result.message.slice(0, 1200) }];
      this.#history = conversation.length > 5 ? [conversation[0], ...conversation.slice(-4)] : conversation;
      this.#transcript.push({ role: 'user', content }, { role: 'assistant', content: result.message,
        guides: (result.guides ?? []).filter(guide => guide.path === "/pages/display").slice(0, 1),
        products: (result.products ?? []).slice(0, 3).map(({ id, handle, reason }) => ({ id, handle, reason })) });
      this.#transcript = this.#transcript.slice(-40);
      this.#pendingContent = '';
      this.#persist();
      await this.#products(result.products ?? [], signal);
      this.refs.status.textContent = '';
    } catch (error) {
      if (!this.isConnected || generation !== this.#generation) return;
      question.remove();
      this.refs.input.value = content;
      this.#pendingContent = '';
      this.#persist();
      this.refs.status.textContent = error instanceof Error && !['TypeError', 'TimeoutError', 'AbortError'].includes(error.name) ? error.message : this.dataset.error;
    } finally {
      if (generation !== this.#generation) return;
      clearTimeout(this.#requestTimer);
      this.#busy = false;
      delete this.dataset.working;
      this.refs.send.disabled = false;
      this.refs.reset.disabled = false;
      this.refs.messages.removeAttribute('aria-busy');
      if (this.refs.dialog.open) this.refs.input.focus();
    }
  }

  #guides(guides = []) {
    for (const guide of guides.slice(0, 1)) {
      if (guide.path !== '/pages/display' || typeof guide.title !== 'string' || typeof guide.description !== 'string') continue;
      const card = document.createElement('a');
      card.className = 'hflex-guide__card';
      card.href = `${this.dataset.root || '/'}pages/display`;
      const text = document.createElement('div'); text.className = 'hflex-guide__card-text';
      const title = document.createElement('strong'); title.textContent = guide.title + ' →'; text.append(title);
      const description = document.createElement('span'); description.textContent = guide.description; text.append(description);
      card.append(text); this.refs.messages.append(card);
    }
    this.refs.messages.scrollTop = this.refs.messages.scrollHeight;
  }

  async #products(products, signal, target = this.refs.messages) {
    if (!products.length) return;
    const root = this.dataset.root || '/';
    let cart;
    try {
      const cartResponse = await fetch(`${root}cart.js`, { signal });
      if (!cartResponse.ok) throw new Error('Cart unavailable');
      cart = await cartResponse.json();
    } catch { if (!signal.aborted && this.isConnected) this.#message(this.dataset.productsError, 'assistant'); return; }
    const cards = await Promise.all(products.slice(0, 3).map(async choice => {
      if (!/^[a-z0-9-]+$/.test(choice.handle) || !/^\d+$/.test(choice.id)) return null;
      try {
        const response = await fetch(`${root}products/${choice.handle}.js`, { signal });
        if (!response.ok) return null;
        const product = await response.json();
        const variant = product.variants?.find(item => String(item.id) === choice.id);
        if (!variant || !Number.isFinite(variant.price)) return null;
        const card = document.createElement('a');
        card.className = 'hflex-guide__card';
        card.href = `${root}products/${choice.handle}?variant=${choice.id}`;
        const imageUrl = product.featured_image ? new URL(product.featured_image, location.origin) : null;
        if (imageUrl?.protocol === 'https:' && (imageUrl.hostname === 'cdn.shopify.com' || imageUrl.origin === location.origin)) {
          const image = document.createElement('img');
          image.src = imageUrl.href;
          image.alt = ''; image.width = 64; image.height = 64; image.loading = 'lazy'; card.append(image);
        }
        const text = document.createElement('div'); text.className = 'hflex-guide__card-text';
        const title = document.createElement('strong'); title.textContent = product.title; text.append(title);
        if (variant.title !== 'Default Title') { const option = document.createElement('span'); option.textContent = variant.title; text.append(option); }
        const price = document.createElement('span'); price.className = 'hflex-guide__price';
        price.textContent = new Intl.NumberFormat(this.dataset.locale || 'sv', { style: 'currency', currency: cart.currency || this.dataset.currency }).format(variant.price / 100); text.append(price);
        const available = document.createElement('span'); available.textContent = variant.available ? this.dataset.available : this.dataset.soldOut; text.append(available);
        const reason = document.createElement('span'); reason.textContent = choice.reason; text.append(reason);
        const link = document.createElement('span'); link.textContent = this.dataset.link + ' →'; text.append(link);
        card.append(text); return card;
      } catch { return null; }
    }));
    if (signal.aborted || !this.isConnected || !target.isConnected) return;
    const verified = cards.filter(Boolean);
    if (!verified.length) { this.#message(this.dataset.productsError, 'assistant'); return; }
    const list = document.createElement('div'); list.className = 'hflex-guide__products'; list.append(...verified);
    target.append(list); this.refs.messages.scrollTop = this.refs.messages.scrollHeight;
  }
}
if (!customElements.get('hflex-assistant')) customElements.define('hflex-assistant', HFlexAssistant);
